import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import './Questionnaire.css';
import './PostPurchase.css';
import './September.css';
import config from '../config/september-config.json';

// ---------------------------------------------------------------------------
// Produktundersøgelse, september 2026. Egen komponent ved siden af de to andre
// skemaer: den har brug for fem ting, de ikke kan, og købsflowet skal ikke
// bære risikoen for en engangsundersøgelse.
//
//   scale         skala 1-5 med plads til «Ved ikke» ved siden af
//   rating-cards  én vurdering pr. emne, med valgfri infoboks og
//                 filtrering af emner efter hvor langt kunden er nået
//   rank          prioritering hvor rækkefølgen er svaret
//   optionsFrom   svarmuligheder hentet fra et tidligere spørgsmål
//   optionsFromRated  samme, men kun de emner der blev vurderet højt nok
//
// Alle spørgsmål kan springes over.
// ---------------------------------------------------------------------------

const M = config.meta;
const STAGES = config.stageOrder || [];

// Lokalt kører skemaet altid i forhåndsvisning, så en gennemgang under
// udvikling aldrig lander i produktionsdatabasen. Backenden accepterer
// desuden kun kald fra forms.celto.dk, så det er bælte og seler.
const erLive = () =>
  typeof window !== 'undefined' && window.location.hostname === 'forms.celto.dk';

const readContext = () => {
  const q = new URLSearchParams(window.location.search);
  return {
    email: (q.get('email') || '').trim(),
    order_id: (q.get('order_id') || '').trim(),
    kilde: (q.get('utm_source') || '').trim(),
  };
};

const erUdloebet = () => {
  // Fristen gælder kun på den rigtige adresse. Ellers ville skemaet aldrig
  // kunne gennemgås lokalt, efter det er lukket — og backenden afviser
  // alligevel indsendelser, så en lokal kopi kan ikke koste en rabatkode.
  if (!erLive()) return false;
  if (!M.deadline) return false;
  const frist = new Date(M.deadline);
  return !isNaN(frist.getTime()) && Date.now() > frist.getTime();
};

// Betingelsesmotor.
//   { field, value: "x" | ["x","y"] }  vis når feltet matcher
//   { field, contains: "x" }           vis når flervalgsfeltet indeholder x
//   { field, gte: 3 } / { lte: 3 }     vis når talsvaret er over/under
//   { field, minLength: 2 }            vis når flervalgsfeltet har mindst n valg
//   { field, answered: true }          vis når feltet overhovedet er besvaret
const conditionMet = (cond, lookup) => {
  if (!cond) return true;
  const v = lookup(cond.field);
  if (cond.answered) {
    if (v === undefined || v === null || v === '') return false;
    if (Array.isArray(v)) return v.length > 0;
    if (typeof v === 'object') return Object.keys(v).length > 0;
    return true;
  }
  if (cond.minLength !== undefined) return Array.isArray(v) && v.length >= cond.minLength;
  if (cond.contains !== undefined) return Array.isArray(v) && v.includes(cond.contains);
  // «Ved ikke» er ikke et tal og må ikke tælle som en høj vurdering.
  if (cond.gte !== undefined) return typeof v === 'number' && v >= cond.gte;
  if (cond.lte !== undefined) return typeof v === 'number' && v <= cond.lte;
  if (Array.isArray(cond.value)) return cond.value.includes(v);
  return v === cond.value;
};

const LINK = /\[([^\]]+)\]\(([^)]+)\)/g;
function Linkify({ text }) {
  const dele = [];
  let sidst = 0;
  let m;
  LINK.lastIndex = 0;
  while ((m = LINK.exec(text)) !== null) {
    if (m.index > sidst) dele.push(text.slice(sidst, m.index));
    const ekstern = /^https?:/i.test(m[2]);
    dele.push(
      <a key={m.index} href={m[2]} {...(ekstern ? { target: '_blank', rel: 'noopener noreferrer' } : {})}>
        {m[1]}
      </a>
    );
    sidst = m.index + m[0].length;
  }
  if (sidst < text.length) dele.push(text.slice(sidst));
  return <>{dele}</>;
}

// Svarmulighederne til et rangeringsspørgsmål. Emner vurderet højt nok kommer
// først; er der færre end der skal rangeres, fyldes op med de næsthøjeste, så
// spørgsmålet ikke bliver umuligt at besvare.
function afledteEmner(q, svar) {
  const kilde = q.optionsFromRated;
  if (!kilde) return [];
  const vurderinger = svar[kilde.field];
  if (!vurderinger || typeof vurderinger !== 'object') return [];

  const kildeQ = config.questions.find(x => x.id === kilde.field) || {};
  const emner = kildeQ.items || [];
  const rang = {};
  (kildeQ.scale || []).forEach((s, i) => { rang[s.value] = i; });

  const scoret = emner
    .map(e => ({ emne: e, svar: vurderinger[e.id] }))
    .filter(x => x.svar !== undefined && x.svar !== kildeQ.naValue)
    .map(x => ({ ...x, score: rang[x.svar] !== undefined ? rang[x.svar] : -1 }))
    .sort((a, b) => b.score - a.score);

  const kvalificeret = scoret.filter(x =>
    kilde.values ? kilde.values.includes(x.svar)
      : (typeof x.svar === 'number' && x.svar >= kilde.min)
  );

  const valgt = kvalificeret.length >= (q.maxRank || 5)
    ? kvalificeret
    : scoret.slice(0, Math.max(kilde.fallbackTop || 8, q.maxRank || 5));

  return valgt.map(x => ({ value: x.emne.id, label: x.emne.label }));
}

function September() {
  const [ctx] = useState(readContext);
  const [svar, setSvar] = useState({});
  const [trin, setTrin] = useState(-1);
  const [faerdig, setFaerdig] = useState(false);
  const [kode, setKode] = useState('');
  // Knapperne spærres under afsendelse, men Enter-tasten går uden om dem.
  // Uden det her værn sender et hold på Enter skemaet igen og igen, og hver
  // gang oprettes en ny rabatkode i Shopify. Det skete: ti koder på ét minut.
  const senderRef = useRef(false);
  const [alleredeBesvaret, setAlleredeBesvaret] = useState(false);
  const [sender, setSender] = useState(false);
  const [fejl, setFejl] = useState('');
  const [lukket] = useState(erUdloebet);

  const forhaandsvisning = !erLive();

  // Livstegn undervejs, så frafaldet kan ses spørgsmål for spørgsmål.
  // september_response får først en række når nogen trykker send, så en der
  // giver op halvvejs efterlader ellers ingen spor.
  //
  // Fire and forget: skemaet venter aldrig på svaret, og alt er pakket ind, så
  // et livstegn der fejler kun koster ét datapunkt — aldrig en besvarelse.
  const sessionRef = useRef(
    Math.random().toString(36).slice(2) + Date.now().toString(36)
  );

  const meldFremdrift = useCallback((felter) => {
    if (forhaandsvisning) return;
    try {
      fetch('https://api.forms.celto.dk/september/progress', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        keepalive: true,
        body: JSON.stringify({ session_id: sessionRef.current, ...felter }),
      }).catch(() => {});
    } catch (e) {
      /* med vilje tavs */
    }
  }, [forhaandsvisning]);

  const lookup = useCallback(f => svar[f], [svar]);

  const synlige = useMemo(
    () => config.questions.filter(q => conditionMet(q.conditional, lookup)),
    [lookup]
  );

  const nu = trin >= 0 ? synlige[trin] : null;

  useEffect(() => {
    if (trin >= 0 && trin >= synlige.length && synlige.length > 0) {
      setTrin(synlige.length - 1);
    }
  }, [trin, synlige.length]);

  // vaerdi må være en funktion af den nuværende værdi. Ratingkort, flervalg
  // og rangering bruger det, så to hurtige klik ikke læser den samme gamle
  // tilstand og taber det første svar.
  const saetSvar = useCallback((id, vaerdi) => {
    setSvar(prev => {
      const ny = typeof vaerdi === 'function' ? vaerdi(prev[id]) : vaerdi;
      const next = { ...prev, [id]: ny };
      // Ryd svar hvis forudsætningen forsvinder, så et skjult spørgsmål ikke
      // sender en gammel værdi med.
      let aendret = true;
      while (aendret) {
        aendret = false;
        for (const q of config.questions) {
          if (q.conditional && q.id in next && !conditionMet(q.conditional, f => next[f])) {
            delete next[q.id];
            aendret = true;
          }
        }
      }
      return next;
    });
  }, []);

  const send = useCallback(async () => {
    if (senderRef.current) return;
    senderRef.current = true;
    if (forhaandsvisning) {
      setKode('CELTO-DEMO-500');
      setFaerdig(true);
      return;
    }
    setSender(true);
    setFejl('');
    try {
      const resp = await fetch('https://api.forms.celto.dk/september/submit', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ ...ctx, answers: svar }),
      });
      if (!resp.ok) throw new Error('HTTP ' + resp.status);
      const data = await resp.json().catch(() => ({}));
      setKode(data.rabatkode || '');
      setFaerdig(true);
      meldFremdrift({
        email: ctx.email || svar.email || null,
        trin: synlige.length,
        antal_svar: Object.keys(svar).length,
        gennemfoert: true,
      });
    } catch (e) {
      setFejl('Vi kunne ikke gemme dine svar. Prøv igen om lidt.');
      // Kun her åbnes der for et nyt forsøg. Lykkedes den, er skemaet færdigt.
      senderRef.current = false;
    } finally {
      setSender(false);
    }
  }, [ctx, svar, forhaandsvisning, synlige.length, meldFremdrift]);

  const naeste = useCallback(() => {
    const q = synlige[trin];
    // Mailen har sin egen besked, fordi en tastefejl her koster konkret:
    // ingen rabatkode, og ingen vej ud af påmindelserne.
    if (q && q.type === 'email') {
      if (!LIGNER_MAIL(String(svar[q.id] || ''))) {
        setFejl(M.emailRequired);
        return;
      }
    } else if (q && !harSvar(q, svar[q.id])) {
      setFejl(M.requiredLabel);
      return;
    }
    setFejl('');
    if (trin + 1 >= synlige.length) { send(); return; }
    setTrin(trin + 1);
  }, [trin, synlige, svar, send]);

  const tilbage = useCallback(() => setTrin(t => Math.max(0, t - 1)), []);

  // Et spørgsmål der kun har ét muligt svar gemmer det selv og springes over.
  useEffect(() => {
    if (!nu || !nu.autoIfSingle || !nu.optionsFrom) return;
    const kilde = svar[nu.optionsFrom];
    if (Array.isArray(kilde) && kilde.length === 1 && svar[nu.id] !== kilde[0]) {
      saetSvar(nu.id, kilde[0]);
    }
  }, [nu, svar, saetSvar]);

  // ---- Tastaturgenveje -----------------------------------------------------
  const naesteRef = useRef(naeste);
  const tilbageRef = useRef(tilbage);
  naesteRef.current = naeste;
  tilbageRef.current = tilbage;

  useEffect(() => {
    if (trin !== -1 || faerdig || lukket) return undefined;
    const start = e => { if (e.key === 'Enter') { e.preventDefault(); setTrin(0); } };
    window.addEventListener('keydown', start);
    return () => window.removeEventListener('keydown', start);
  }, [trin, faerdig, lukket]);

  useEffect(() => {
    if (trin < 0 || faerdig || lukket) return undefined;
    const q = synlige[trin];
    if (!q) return undefined;

    const handler = e => {
      const tag = (e.target.tagName || '').toLowerCase();
      const iTekst = tag === 'input' || tag === 'textarea';
      if (e.key === 'Enter' && !(tag === 'textarea' && e.shiftKey)) {
        e.preventDefault(); naesteRef.current(); return;
      }
      if (iTekst || e.metaKey || e.ctrlKey || e.altKey) return;
      if (e.key === 'ArrowDown') { e.preventDefault(); naesteRef.current(); return; }
      if (e.key === 'ArrowUp') { e.preventDefault(); tilbageRef.current(); return; }

      if (q.type === 'scale') {
        const n = Number(e.key);
        if (q.scale.some(s => s.value === n)) {
          e.preventDefault();
          saetSvar(q.id, n);
          setTimeout(() => naesteRef.current(), 220);
        }
        return;
      }
      if (q.type !== 'radio') return;
      if (e.key.length !== 1) return;
      const muligheder = q.optionsFrom ? [] : (q.options || []);
      const opt = muligheder[e.key.toUpperCase().charCodeAt(0) - 65];
      if (!opt) return;
      e.preventDefault();
      saetSvar(q.id, opt.value);
      setTimeout(() => naesteRef.current(), 160);
    };
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, [trin, faerdig, lukket, synlige, saetSvar]);

  // Ét livstegn pr. spørgsmål man når frem til.
  useEffect(() => {
    if (trin < 0 || faerdig) return;
    const q = synlige[trin];
    meldFremdrift({
      email: ctx.email || svar.email || null,
      spoergsmaal: q ? q.id : null,
      trin,
      antal_svar: Object.keys(svar).length,
      gennemfoert: false,
    });
    // Kun trin: ét livstegn pr. spørgsmål, ikke ét pr. tastetryk.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [trin]);

  // Mailen i linket fortæller hvem der kom. Har de svaret før, siger vi det,
  // i stedet for at lade dem bruge et kvarter på det samme igen.
  useEffect(() => {
    if (forhaandsvisning || !ctx.email) return;
    let afbrudt = false;
    fetch('https://api.forms.celto.dk/september/status?email='
          + encodeURIComponent(ctx.email))
      .then(r => (r.ok ? r.json() : null))
      .then(d => { if (!afbrudt && d && d.besvaret) setAlleredeBesvaret(true); })
      .catch(() => {});
    return () => { afbrudt = true; };
  }, [ctx.email, forhaandsvisning]);

  // ---- Skærmbilleder -------------------------------------------------------

  if (alleredeBesvaret && !faerdig) {
    return (
      <Ramme>
        <div className="tf-completion pp-completion">
          <h2 className="tf-completion-title">{M.alreadyTitle}</h2>
          <p>{M.alreadyBody}</p>
        </div>
      </Ramme>
    );
  }

  if (lukket) {
    return (
      <Ramme>
        <div className="tf-completion pp-completion">
          <h2 className="tf-completion-title">{M.closedTitle}</h2>
          <p>{M.closedBody}</p>
        </div>
      </Ramme>
    );
  }

  if (faerdig) {
    return (
      <Ramme forhaandsvisning={forhaandsvisning}>
        <div className="tf-completion pp-completion">
          <h2 className="tf-completion-title">{M.thanksTitle}</h2>
          <p>{M.thanksBody}</p>
          <div className="sep-kode-boks">
            <p className="sep-kode-label">{M.codeLabel}</p>
            {kode
              ? <p className="sep-kode">{kode}</p>
              : <p className="pp-note"><Linkify text={M.codePending} /></p>}
            <p className="pp-note">{M.codeNote}</p>
          </div>
          <Vilkaar />
          <p style={{ marginTop: 22 }}>
            <a className="tf-btn-primary sep-btn-link" href={M.thanksCtaHref} target="_blank" rel="noopener noreferrer">
              {M.thanksCtaLabel}
            </a>
          </p>
          {forhaandsvisning && (
            <div className="pp-preview-dump">
              <p className="pp-preview-dump-title">
                Forhåndsvisning — intet er sendt. {Object.keys(svar).length} besvarede felter:
              </p>
              <pre>{JSON.stringify(svar, null, 2)}</pre>
            </div>
          )}
        </div>
      </Ramme>
    );
  }

  if (trin === -1) {
    return (
      <Ramme forhaandsvisning={forhaandsvisning}>
        <div className="pp-intro">
          <h1 className="pp-intro-title">{M.introTitle}</h1>
          {M.introBody.map((t, i) => (
            <p key={i} className={i === 0 ? 'pp-intro-lead' : 'pp-intro-note'}>{t}</p>
          ))}
          <p className="pp-intro-privacy">{M.introPrivacy}</p>
          <button type="button" className="tf-btn-primary" onClick={() => setTrin(0)}>
            {M.introCta}
            <span className="tf-btn-arrow" aria-hidden="true">→</span>
          </button>
        </div>
      </Ramme>
    );
  }

  if (!nu) return null;

  const sidste = trin + 1 >= synlige.length;
  const pct = Math.round((trin / Math.max(synlige.length, 1)) * 100);
  const svaret = nu.type === 'email'
    ? LIGNER_MAIL(String(svar[nu.id] || ''))
    : harSvar(nu, svar[nu.id]);

  return (
    <Ramme forhaandsvisning={forhaandsvisning}>
      <div className="tf-progress"><div className="tf-progress-bar" style={{ width: pct + '%' }} /></div>
      <div className="tf-step">
        <div className="tf-step-content" key={nu.id}>
          {nu.section && <p className="sep-sektion">{nu.section}</p>}
          <h2 className="pp-label">{nu.label}</h2>
          {nu.help && <p className="tf-description">{nu.help}</p>}
          {nu.note && <p className="sep-note">{nu.note}</p>}

          <div className="tf-input-wrap">
            <Felt q={nu} value={svar[nu.id]} svar={svar} onChange={saetSvar} onAdvance={naeste} />
          </div>

          {fejl && <div className="tf-error" role="alert">{fejl}</div>}

          <div className="tf-actions">
            <button
              type="button"
              className="tf-btn-primary"
              onClick={naeste}
              disabled={sender || !svaret}
            >
              {sidste
                ? (sender ? M.submitLoadingLabel : M.submitLabel)
                : (nu.optional && !harSvar({ ...nu, optional: false }, svar[nu.id])
                    ? M.skipLabel
                    : M.nextLabel)}
              {!sender && <span className="tf-btn-arrow" aria-hidden="true">↵</span>}
            </button>
          </div>

          {trin > 0 && (
            <button type="button" className="pp-back" onClick={tilbage}>← {M.backLabel}</button>
          )}
        </div>
      </div>
    </Ramme>
  );
}

function Ramme({ children, forhaandsvisning }) {
  return (
    <div className={`tf-container ${forhaandsvisning ? 'pp-har-banner' : ''}`}>
      {forhaandsvisning && (
        <div className="pp-preview" role="status">
          <strong>Forhåndsvisning</strong> — svar bliver ikke gemt, og rabatkoden er en pladsholder.
        </div>
      )}
      <div className="pp-topbar">
        <img
          className="tf-wordmark"
          src={`${process.env.PUBLIC_URL || ''}/celto-wordmark.png`}
          alt="Celto"
        />
      </div>
      {children}
    </div>
  );
}

function Vilkaar() {
  return (
    <div className="sep-vilkaar">
      <p className="sep-vilkaar-titel">{M.termsHeading}</p>
      <ul>{M.termsShort.map((t, i) => <li key={i}>{t}</li>)}</ul>
    </div>
  );
}

// ---------------------------------------------------------------------------

function Felt({ q, value, svar, onChange, onAdvance }) {
  switch (q.type) {
    case 'radio': return <Radio q={q} value={value} svar={svar} onChange={onChange} onAdvance={onAdvance} />;
    case 'checkbox': return <Checkbox q={q} value={value} onChange={onChange} />;
    case 'scale': return <Skala q={q} value={value} onChange={onChange} onAdvance={onAdvance} />;
    case 'rating-cards': return <Ratingkort q={q} value={value} svar={svar} onChange={onChange} />;
    case 'rank': return <Rangering q={q} value={value} svar={svar} onChange={onChange} />;
    case 'text':
    case 'textarea': return <Fritekst q={q} value={value} onChange={onChange} />;
    case 'email': return <Mail q={q} value={value} onChange={onChange} />;
    default: return null;
  }
}

// Svarmuligheder: enten faste, eller hentet fra et tidligere flervalg.
function muligheder(q, svar) {
  if (!q.optionsFrom) return q.options || [];
  const valgte = svar[q.optionsFrom];
  if (!Array.isArray(valgte)) return [];
  const kilde = config.questions.find(x => x.id === q.optionsFrom);
  const alle = (kilde && kilde.options) || [];
  return alle.filter(o => valgte.includes(o.value));
}

function Radio({ q, value, svar, onChange, onAdvance }) {
  const opts = muligheder(q, svar);
  return (
    <div className="tf-radio-group" role="radiogroup" aria-label={q.label}>
      {opts.map((opt, i) => {
        const valgt = value === opt.value;
        return (
          <button
            key={opt.value}
            type="button"
            role="radio"
            aria-checked={valgt}
            className={`tf-radio-card ${valgt ? 'tf-radio-card--selected' : ''}`}
            onClick={() => {
              if (valgt) { onChange(q.id, ''); return; }
              onChange(q.id, opt.value);
              setTimeout(onAdvance, 160);
            }}
          >
            <span className="tf-radio-letter">{String.fromCharCode(65 + i)}</span>
            <span className="tf-radio-text"><span className="tf-radio-label">{opt.label}</span></span>
            {valgt && <span className="tf-radio-check" aria-hidden="true">✓</span>}
          </button>
        );
      })}
    </div>
  );
}

function Checkbox({ q, value, onChange }) {
  const valgte = Array.isArray(value) ? value : [];
  const fuld = q.maxSelect != null && valgte.length >= q.maxSelect;

  const toggle = kode => {
    onChange(q.id, forrige => {
      const nu = Array.isArray(forrige) ? forrige : [];
      if (nu.includes(kode)) return nu.filter(v => v !== kode);
      // «Ingen af ovenstående» og lignende udelukker resten.
      if (q.exclusive && kode === q.exclusive) return [kode];
      const uden = q.exclusive ? nu.filter(v => v !== q.exclusive) : nu;
      if (q.maxSelect != null && uden.length >= q.maxSelect) return nu;
      return [...uden, kode];
    });
  };

  return (
    <>
      <div className="tf-radio-group" role="group" aria-label={q.label}>
        {(q.options || []).map((opt, i) => {
          const paa = valgte.includes(opt.value);
          const spaerret = fuld && !paa && opt.value !== q.exclusive;
          return (
            <button
              key={opt.value}
              type="button"
              role="checkbox"
              aria-checked={paa}
              aria-disabled={spaerret}
              className={`tf-radio-card ${paa ? 'tf-radio-card--selected' : ''} ${spaerret ? 'pp-card--muted' : ''}`}
              onClick={() => toggle(opt.value)}
            >
              <span className="tf-radio-letter tf-radio-letter--check">{String.fromCharCode(65 + i)}</span>
              <span className="tf-radio-text"><span className="tf-radio-label">{opt.label}</span></span>
              {paa && <span className="tf-radio-check" aria-hidden="true">✓</span>}
            </button>
          );
        })}
      </div>
      {q.maxSelect != null && (
        <p className="pp-counter">{valgte.length} af højst {q.maxSelect} valgt</p>
      )}
    </>
  );
}

function Skala({ q, value, onChange, onAdvance }) {
  const sidst = q.scale[q.scale.length - 1];
  return (
    <div className="pp-rating">
      <p className="pp-skala"><span>1 = {q.scale[0].label}</span><span aria-hidden="true"> · </span><span>{sidst.value} = {sidst.label}</span></p>
      <div className="pp-rating-row" role="radiogroup" aria-label={q.label}>
        {q.scale.map(s => {
          const valgt = value === s.value;
          return (
            <button
              key={s.value}
              type="button"
              role="radio"
              aria-checked={valgt}
              aria-label={`${s.value} – ${s.label}`}
              className={`pp-rating-btn ${valgt ? 'pp-rating-btn--selected' : ''}`}
              onClick={() => {
                if (valgt) { onChange(q.id, undefined); return; }
                onChange(q.id, s.value);
                setTimeout(onAdvance, 220);
              }}
            >
              <span className="pp-rating-num">{s.value}</span>
              <span className="pp-rating-text">{s.label}</span>
            </button>
          );
        })}
      </div>
      {(q.extra || []).map(e => (
        <button
          key={e.value}
          type="button"
          className={`pp-na ${value === e.value ? 'pp-na--selected' : ''}`}
          onClick={() => {
            if (value === e.value) { onChange(q.id, undefined); return; }
            onChange(q.id, e.value);
            setTimeout(onAdvance, 220);
          }}
        >
          {e.label}
        </button>
      ))}
    </div>
  );
}

function Ratingkort({ q, value, svar, onChange }) {
  const [aabenInfo, setAabenInfo] = useState(null);
  const givet = value && typeof value === 'object' ? value : {};

  // Kategorier der forudsætter et senere stadie i forløbet vises ikke til dem
  // der ikke er nået dertil. Ellers beder vi folk vurdere noget de aldrig har
  // oplevet, og svarene bliver støj.
  const naaetTil = q.stageField ? STAGES.indexOf(svar[q.stageField]) : STAGES.length;
  const emner = (q.items || []).filter(
    e => e.minStage === undefined || naaetTil < 0 || naaetTil >= e.minStage
  );

  const saet = (emneId, v) => {
    onChange(q.id, forrige => {
      const næste = { ...(forrige && typeof forrige === 'object' ? forrige : {}) };
      if (næste[emneId] === v) delete næste[emneId];
      else næste[emneId] = v;
      return næste;
    });
  };

  const besvaret = emner.filter(e => givet[e.id] !== undefined).length;

  return (
    <div className="pp-matrix">
      <p className="pp-skala">
        {q.scale.map((s, i) => (
          <span key={s.value}>
            {i > 0 && <span aria-hidden="true"> · </span>}
            {typeof s.value === 'number' ? `${s.value} = ${s.label}` : s.label}
          </span>
        ))}
      </p>
      {emner.map(e => (
        <div className="pp-matrix-card" key={e.id}>
          <p className="pp-matrix-label">
            {e.label}
            {e.info && (
              <button
                type="button"
                className="sep-info-knap"
                aria-label="Hvad betyder det?"
                onClick={() => setAabenInfo(aabenInfo === e.id ? null : e.id)}
              >
                i
              </button>
            )}
          </p>
          {e.info && aabenInfo === e.id && <p className="sep-info-boks">{e.info}</p>}
          <div
            className={`pp-matrix-row ${q.scale.length === 4 ? 'sep-row-4' : ''}`}
            role="radiogroup"
            aria-label={e.label}
          >
            {q.scale.map(s => {
              const valgt = givet[e.id] === s.value;
              return (
                <button
                  key={String(s.value)}
                  type="button"
                  role="radio"
                  aria-checked={valgt}
                  aria-label={`${e.label}: ${s.label}`}
                  title={s.label}
                  className={`pp-matrix-btn ${valgt ? 'pp-matrix-btn--selected' : ''}`}
                  onClick={() => saet(e.id, s.value)}
                >
                  {s.short || s.value}
                </button>
              );
            })}
          </div>
          <button
            type="button"
            className={`pp-na ${givet[e.id] === q.naValue ? 'pp-na--selected' : ''}`}
            onClick={() => saet(e.id, q.naValue)}
          >
            {q.naLabel}
          </button>
        </div>
      ))}
      <p className="pp-counter">{besvaret} af {emner.length} vurderet</p>
    </div>
  );
}

function Rangering({ q, value, svar, onChange }) {
  const opts = afledteEmner(q, svar);
  const raekke = Array.isArray(value) ? value : [];

  const toggle = kode => {
    onChange(q.id, forrige => {
      const nu = Array.isArray(forrige) ? forrige : [];
      if (nu.includes(kode)) return nu.filter(v => v !== kode);
      if (nu.length >= (q.maxRank || 5)) return nu;
      return [...nu, kode];
    });
  };

  if (!opts.length) {
    return <p className="sep-note">Der er ingen emner at prioritere. Spring videre.</p>;
  }

  return (
    <>
      <div className="tf-radio-group" role="group" aria-label={q.label}>
        {opts.map(opt => {
          const plads = raekke.indexOf(opt.value);
          const valgt = plads >= 0;
          const fuld = raekke.length >= (q.maxRank || 5);
          return (
            <button
              key={opt.value}
              type="button"
              aria-pressed={valgt}
              className={`tf-radio-card ${valgt ? 'tf-radio-card--selected' : ''} ${!valgt && fuld ? 'pp-card--muted' : ''}`}
              onClick={() => toggle(opt.value)}
            >
              <span className={`tf-radio-letter ${valgt ? 'sep-rang' : ''}`}>
                {valgt ? plads + 1 : ''}
              </span>
              <span className="tf-radio-text"><span className="tf-radio-label">{opt.label}</span></span>
            </button>
          );
        })}
      </div>
      <p className="pp-counter">{raekke.length} af {q.maxRank || 5} prioriteret</p>
    </>
  );
}

// Mailadressen er det eneste felt hvor en tastefejl koster noget konkret:
// respondenten får ikke sin kode, og et «ja» til interview bliver ubrugeligt.
// Derfor en synlig, blid advarsel frem for at blokere — feltet er stadig
// frivilligt, og koden vises på næste side uanset hvad.
const LIGNER_MAIL = (v) => /^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/.test(v.trim());

// Alle spørgsmål skal besvares. Et enkelt kan fritages med "optional": true i
// konfigurationen — mekanismen findes, så det er én linje at åbne igen, hvis
// et bestemt spørgsmål viser sig at koste for mange gennemførsler.
const harSvar = (q, v) => {
  if (q.optional) return true;
  if (v === undefined || v === null || v === '') return false;
  if (Array.isArray(v)) return v.length > 0;
  if (typeof v === 'object') return Object.keys(v).length > 0;
  return true;
};

function Mail({ q, value, onChange }) {
  const v = value || '';
  const advar = v.trim().length > 3 && !LIGNER_MAIL(v);
  return (
    <>
      <input
        className="tf-input"
        type="email"
        inputMode="email"
        autoComplete="email"
        placeholder="navn@eksempel.dk"
        value={v}
        maxLength={q.maxLength}
        onChange={e => onChange(q.id, e.target.value.slice(0, q.maxLength || 255))}
      />
      {advar && (
        <p className="pp-counter pp-counter--lav">
          Det ligner ikke en mailadresse. Tjek den, ellers kan vi ikke sende koden.
        </p>
      )}
    </>
  );
}

function Fritekst({ q, value, onChange }) {
  const v = value || '';
  const tilbage = q.maxLength ? q.maxLength - v.length : null;
  const Tag = q.type === 'textarea' ? 'textarea' : 'input';
  return (
    <>
      <Tag
        className={q.type === 'textarea' ? 'tf-textarea' : 'tf-input'}
        value={v}
        maxLength={q.maxLength}
        rows={q.type === 'textarea' ? (q.rows || 4) : undefined}
        placeholder={q.type === 'text' ? M.otherPlaceholder : undefined}
        onChange={e => onChange(q.id, e.target.value.slice(0, q.maxLength || undefined))}
      />
      {q.maxLength != null && (
        <p className={`pp-counter ${tilbage < 40 ? 'pp-counter--lav' : ''}`}>{tilbage} tegn tilbage</p>
      )}
    </>
  );
}

export default September;
